<template>
  <ul v-if="checks.length" class="check-list">
    <li
      v-for="check in checks"
      :key="check.result_id || check.requirement_code || check.requirement_name"
      :class="rowClass(check)"
    >
      <span class="check-mark">{{ mark(check) }}</span>
      <div class="check-body">
        <strong>{{ check.requirement_name }}</strong>
        <p v-if="check.evidence && !check.waived">{{ check.evidence }}</p>
        <p v-if="check.waived && check.waived_reason" class="waiver-reason">
          Waived: {{ check.waived_reason }}
        </p>
        <button
          v-if="canWaive(check) && openKey !== rowKey(check)"
          type="button"
          class="btn btn-outline btn-sm waive-btn"
          @click="startWaive(check)"
        >
          Waive with a reason
        </button>
        <form
          v-if="canWaive(check) && openKey === rowKey(check)"
          class="waive-form"
          @submit.prevent="submitWaive(check)"
        >
          <p class="waive-hint">
            Accept this requirement for this insured. This is the same waiver the compliance agent saves, and it stays in place on future checks until someone revokes it.
          </p>
          <label>
            Reason
            <textarea
              v-model="reason"
              rows="2"
              required
              placeholder="Why does this requirement not apply to this insured?"
            />
          </label>
          <p v-if="error" class="form-error">{{ error }}</p>
          <div class="form-actions">
            <button type="submit" class="btn btn-primary btn-sm" :disabled="saving || !reason.trim()">
              {{ saving ? 'Saving waiver…' : 'Save waiver' }}
            </button>
            <button type="button" class="btn btn-ghost btn-sm" :disabled="saving" @click="cancel">
              Cancel
            </button>
          </div>
        </form>
        <button
          v-if="check.waived && check.waiver_id"
          type="button"
          class="btn btn-ghost btn-sm waive-btn"
          :disabled="saving"
          @click="revoke(check)"
        >
          Revoke waiver
        </button>
      </div>
    </li>
  </ul>
</template>

<script>
import { ref } from 'vue';
import api from '@/services/api';

export default {
  name: 'EntityPolicyCheckList',
  props: {
    checks: { type: Array, default: () => [] },
    subjectId: { type: [String, Number], required: true },
  },
  emits: ['waived'],
  setup(props, { emit }) {
    const openKey = ref('');
    const reason = ref('');
    const saving = ref(false);
    const error = ref('');

    const rowKey = (check) => String(check.result_id || check.requirement_code || check.requirement_name);
    const rowClass = (check) => {
      if (check.passed) return 'passed';
      if (check.waived) return 'waived';
      return 'failed';
    };
    const mark = (check) => {
      if (check.passed) return 'Met';
      if (check.waived) return 'Waived';
      return 'Gap';
    };
    const canWaive = (check) => !check.passed && !check.waived && Boolean(check.requirement_code);

    const startWaive = (check) => {
      openKey.value = rowKey(check);
      reason.value = '';
      error.value = '';
    };
    const cancel = () => {
      openKey.value = '';
      reason.value = '';
      error.value = '';
    };

    const errorMessage = (err) => {
      const data = err.response?.data;
      if (!data) return err.message || 'Could not save this waiver.';
      if (typeof data === 'string') return data;
      if (data.error) return data.error;
      if (data.detail) return data.detail;
      const reasonError = data.reason;
      if (Array.isArray(reasonError) && reasonError.length) return reasonError[0];
      return 'Could not save this waiver.';
    };

    const submitWaive = async (check) => {
      const text = reason.value.trim();
      if (!text || saving.value) return;
      saving.value = true;
      error.value = '';
      try {
        await api.post('/compliance-waivers/', {
          compliance_subject: props.subjectId,
          requirement_code: check.requirement_code,
          requirement_name: check.requirement_name,
          reason: text,
          scope: 'SUBJECT',
        });
        cancel();
        emit('waived');
      } catch (err) {
        error.value = errorMessage(err);
      } finally {
        saving.value = false;
      }
    };

    const revoke = async (check) => {
      if (!check.waiver_id || saving.value) return;
      const confirmed = window.confirm(
        `Revoke the waiver for "${check.requirement_name}"? It will count as a gap again.`,
      );
      if (!confirmed) return;
      saving.value = true;
      error.value = '';
      try {
        await api.post(`/compliance-waivers/${check.waiver_id}/revoke/`, {
          reason: 'Revoked from the coverage tab',
        });
        emit('waived');
      } catch (err) {
        error.value = errorMessage(err);
      } finally {
        saving.value = false;
      }
    };

    return {
      openKey,
      reason,
      saving,
      error,
      rowKey,
      rowClass,
      mark,
      canWaive,
      startWaive,
      cancel,
      submitWaive,
      revoke,
    };
  },
};
</script>

<style scoped>
.check-list {
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0;
}

.check-list li {
  display: flex;
  gap: 0.6rem;
  padding: 0.45rem 0;
  border-bottom: 1px solid #f1f5f9;
}

.check-body {
  flex: 1;
  min-width: 0;
}

.check-list p {
  margin: 0.15rem 0 0;
  color: #475569;
  font-size: 0.8rem;
}

.check-mark {
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.passed .check-mark { color: #047857; }
.failed .check-mark { color: #b91c1c; }
.waived .check-mark { color: #6d28d9; }

.waiver-reason {
  color: #5b21b6;
}

.waive-btn {
  margin-top: 0.4rem;
}

.waive-form {
  margin-top: 0.45rem;
  padding: 0.65rem 0.7rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}

.waive-hint {
  margin: 0 0 0.5rem;
}

.waive-form label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  color: #475569;
}

.waive-form textarea {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  padding: 0.4rem 0.5rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font: inherit;
  resize: vertical;
}

.form-actions {
  display: flex;
  gap: 0.4rem;
  margin-top: 0.5rem;
}

.form-error {
  color: #b91c1c;
  margin-top: 0.35rem;
}
</style>
